import React, { createContext, useState, useCallback, useEffect } from 'react';
import { auth, db } from '../config/firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get user data from Firestore
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const userInfo = {
              id: firebaseUser.uid,
              email: firebaseUser.email,
              name: userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
              role: userData.role || 'student',
              firstName: userData.firstName,
              lastName: userData.lastName
            };
            setUser(userInfo);

            // Get Firebase ID token
            const idToken = await firebaseUser.getIdToken();
            setToken(idToken);
            localStorage.setItem('token', idToken);
            localStorage.setItem('user', JSON.stringify(userInfo));
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
        }
      } else {
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const register = useCallback(async (name, email, password, role = 'student') => {
    setLoading(true);
    setError(null);
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      const nameParts = name.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || 'User';

      // Create user document in Firestore
      const userData = {
        firstName,
        lastName,
        name,
        email: email.toLowerCase(),
        role: role.toLowerCase(),
        profileImage: null,
        bio: '',
        enrolledCourses: [],
        createdCourses: [],
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), userData);

      // Get ID token
      const idToken = await firebaseUser.getIdToken();

      const userInfo = {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        name,
        role: role.toLowerCase(),
        firstName,
        lastName
      };

      setUser(userInfo);
      setToken(idToken);
      localStorage.setItem('token', idToken);
      localStorage.setItem('user', JSON.stringify(userInfo));

      return { success: true, user: userInfo, token: idToken };
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      console.log('[AUTH] Firebase login attempt for:', email);

      // Sign in with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Get user data from Firestore
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error('User data not found');
      }

      const userData = userDoc.data();
      const idToken = await firebaseUser.getIdToken();

      const userInfo = {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        name: userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        role: userData.role || 'student',
        firstName: userData.firstName,
        lastName: userData.lastName
      };

      setUser(userInfo);
      setToken(idToken);
      localStorage.setItem('token', idToken);
      localStorage.setItem('user', JSON.stringify(userInfo));

      console.log('[AUTH] Firebase login successful');
      return { success: true, user: userInfo, token: idToken };
    } catch (err) {
      console.error('[AUTH] Firebase login error:', err);
      const errorMsg = err.message || 'Login failed';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (err) {
      console.error('Logout error:', err);
    }
  }, []);

  const value = {
    user,
    token,
    loading,
    error,
    register,
    login,
    logout,
    setUser,
    setToken,
    isAuthenticated: !!user,
    isInstructor: user?.role === 'instructor',
    isStudent: user?.role === 'student'
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#ffffff' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '1.1rem', color: '#666' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
